import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import {
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Select,
  TextArea,
  Message,
} from 'semantic-ui-react';
import PropTypes from 'prop-types';
import client from '../../../apollo';
import {
  AuctionSystem,
  DaysOfWeek,
  LocationState,
} from '../../../utilities/commonData';
import { utcMoment } from '../../../utilities/dateUtils';
import validation, {
  ValidationType,
  filterNonDigitInput,
} from '../../../utilities/validation';
import auctionLocationQuery from '../../../graphql/auctionLocationQuery';

export const AuctionLocationAction = {
  Create: 'Create',
  Update: 'Update',
  Delete: 'Delete',
};

const auctionLocationInit = {
  locationName: '',
  locationCode: '',
  externalAuctionCode: '',
  auctionSystem: '',
  purchasesEnabled: false,
  acceptsReturn: false,
  inspectionsEnabled: false,
  dsPaidPsiEnabled: false,
  saleDays: [],
  titleDays: '',
  launchDate: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  locationArea: '',
  dealerNumber: '',
  supplierNumber: '',
  macolaInventoryCode: '',
  returnInstructions: '',
  auctionNotes: '',
  returnEmails: '',
  externalAuctionEmails: '',
  additionalEmails: '',
};

const notRequiredExternal = ['BSC', 'MANHEIM'];
function AuctionLocationForm(props) {
  const {
    isModalOpen,
    onCloseHandler,
    onSubmitHandler,
    onCloseError,
    selectedLocation,
    locationAction,
    isLoading,
    submitError,
  } = props;
  const [formData, setFormData] = useState(auctionLocationInit);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [auctionOptions, setAuctionOptions] = useState([]);
  const [isRequireExternal, setIsRequireExternal] = useState(true);
  const [flashMessage, setFlashMessage] = useState('');

  useEffect(() => {
    setAuctionOptions(AuctionSystem);
  }, []);

  const saleDayOption = DaysOfWeek.map(value => ({
    key: value,
    text: value,
    value,
  }));

  const isOnlyNumbers = text => {
    return /^\d+$/.test(text);
  };

  const filterAuctionOptions = value => {
    setAuctionOptions(prev => prev.map(partner => {
      const onlyNumberCase = ['ASI', 'AOS', 'XCIRA', 'AUCTION STREAMING'];
      const mixedCase = ['BSC', 'MANHEIM'];
      let disabled = false;

      if (value) {
        if (isOnlyNumbers(value)) {
          disabled = mixedCase.includes(partner.value);
        } else {
          disabled = onlyNumberCase.includes(partner.value);
        }
      }

      return { ...partner, disabled };
    }));
  };

  const getLocationWhenFormatLaunchDate = location => {
    const formattedLocation = { ...location };
    if (formattedLocation.launchDate) {
      formattedLocation.launchDate = moment.utc(formattedLocation.launchDate).format();
    }
    return formattedLocation;
  };

  // Edit function
  useEffect(() => {
    if (selectedLocation) {
      setFormData(getLocationWhenFormatLaunchDate(selectedLocation));
      filterAuctionOptions(selectedLocation.locationCode);
      if (notRequiredExternal.includes(selectedLocation.auctionSystem)) {
        setIsRequireExternal(false);
      } else {
        setIsRequireExternal(true);
      }
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (auctionOptions) {
      const unavaliableOption = auctionOptions
        .filter(partner => partner.disabled)
        .map(partner => partner.value);
      if (
        unavaliableOption.length
        && formData.auctionSystem
        && unavaliableOption.includes(formData.auctionSystem.toUpperCase())
      ) {
        setFormData(prev => {
          return { ...prev, auctionSystem: '', externalAuctionCode: '' };
        });

        // Reset requirement of external auction code
        setIsRequireExternal(true);

        // Clear errors
        setErrors(prev => ({
          ...prev,
          auctionSystem: [],
          externalAuctionCode: [],
        }));
      }
    }
  }, [auctionOptions]);

  useEffect(() => {
    setFlashMessage(submitError);
    setLoading(false);
  }, [submitError]);

  useEffect(() => {
    setLoading(isLoading);
    if (!isLoading && !isModalOpen) {
      setFormData(auctionLocationInit);
    }
  }, [isLoading, isModalOpen]);

  // Reset Auction System when modal is closed
  useEffect(() => {
    if (!isModalOpen) {
      setAuctionOptions(AuctionSystem);
    }
  }, [isModalOpen]);

  const checkNameExist = async() => {
    if (!formData.locationName) return false;
    const apmsLocationList = await client.query({
      query: auctionLocationQuery,
      variables: {
        auctionLocationSearch: formData.locationName,
        page: 1,
        size: 200,
      },
    });
    let locations = await apmsLocationList.data.apmsLocationList;
    if (locations) {
      if (selectedLocation) {
        locations = locations.filter(
          l => l.locationName !== selectedLocation.locationName
        );
      }
      return locations.some(l => l.locationName === formData.locationName);
    }
    return false;
  };

  const submitForm = useCallback(
    async e => {
      e.preventDefault();

      if (loading) return;

      setLoading(true);
      const isNameExist = await checkNameExist();
      const locationNameRules = [
        { type: ValidationType.required },
        { type: ValidationType.maxLength, value: 64 },
      ];

      const notExceedMsg = (field, limit) => `${field} must not exceed ${limit} characters.`;
      const locationNameRulesErrors = {
        required:
          'Auction Name is required. Please enter a valid name for the auction location',
        maxLength: notExceedMsg('Name', 64),
      };
      if (isNameExist) {
        locationNameRules.push({ type: ValidationType.defaultError });
        locationNameRulesErrors.defaultError = `${
          formData.locationName
        }' already exists in the system. Try using a different name.`;
      }
      const locationNameErrors = validation(
        formData.locationName,
        locationNameRules,
        locationNameRulesErrors
      );
      const locationCodeErrors = validation(
        formData.locationCode,
        [
          { type: ValidationType.required },
          { type: ValidationType.maxLength, value: 8 },
        ],
        {
          required:
            'Location/IMS Code is required. Please provide the IMS code for the auction location.',
          maxLength: notExceedMsg('Location Code', 8),
        }
      );

      const externalAuctionCodeRules = [
        { type: ValidationType.maxLength, value: 10 },
      ];
      const externalAuctionCodeErrors = {
        maxLength: notExceedMsg('External Auction Code', 10),
      };
      if (isRequireExternal) {
        externalAuctionCodeRules.push({ type: ValidationType.required });
        externalAuctionCodeErrors.required = 'External Auction Code is required. '
          + 'Please input the external identifier for this auction.';
      }
      const externalCodeErrors = validation(
        formData.externalAuctionCode,
        externalAuctionCodeRules,
        externalAuctionCodeErrors
      );
      const auctionSystemErrors = validation(
        formData.auctionSystem,
        [
          { type: ValidationType.required },
          { type: ValidationType.maxLength, value: 100 },
        ],
        {
          required:
            'Auction System selection is required. Please choose a system from the dropdown list.',
          maxLength: notExceedMsg('Auction System', 100),
        }
      );
      const addressErrors = validation(
        formData.address,
        [
          { type: ValidationType.required },
          { type: ValidationType.maxLength, value: 500 },
        ],
        {
          required:
            'Address is required. Please input address for this auction.',
          maxLength: notExceedMsg('Location Street', 500),
        }
      );
      const cityErrors = validation(
        formData.city,
        [
          { type: ValidationType.required },
          { type: ValidationType.maxLength, value: 100 },
        ],
        {
          required: 'City is required. Please input the city for this auction.',
          maxLength: notExceedMsg('City', 100),
        }
      );
      const stateErrors = validation(
        formData.state,
        [{ type: ValidationType.required }],
        { required: 'State is required. Please select a state from the list.' }
      );
      const zipErrors = validation(
        formData.zip,
        [
          { type: ValidationType.required },
          { type: ValidationType.maxLength, value: 50 },
        ],
        {
          required: 'Zip is required. Please input the zip for this auction.',
          maxLength: notExceedMsg('Zip', 50),
        }
      );
      const locationAreaErrors = validation(
        formData.locationArea,
        [{ type: ValidationType.maxLength, value: 64 }],
        { maxLength: notExceedMsg('Location Area', 64) }
      );
      const saleDaysValue = formData.saleDays
        ? formData.saleDays.join(',')
        : '';
      const saleDaysErrors = validation(
        saleDaysValue,
        [{ type: ValidationType.maxLength, value: 100 }],
        { maxLength: notExceedMsg('Sale Days', 100) }
      );
      const macolaInventoryCodeErrors = validation(
        formData.macolaInventoryCode,
        [{ type: ValidationType.maxLength, value: 50 }],
        { maxLength: notExceedMsg('Macola Inventory Code', 50) }
      );
      const returnInstructionsErrors = validation(
        formData.returnInstructions,
        [{ type: ValidationType.maxLength, value: 500 }],
        { maxLength: notExceedMsg('Return Instructions', 500) }
      );
      const auctionNotesErrors = validation(
        formData.auctionNotes,
        [{ type: ValidationType.maxLength, value: 200 }],
        { maxLength: notExceedMsg('Auction Notes', 200) }
      );
      const titleDaysErrors = validation(
        formData.titleDays,
        [{ type: ValidationType.maxLength, value: 7 }],
        { maxLength: notExceedMsg('Title Days', 7) }
      );
      const dealerNumberErrors = validation(
        formData.dealerNumber,
        [{ type: ValidationType.maxLength, value: 7 }],
        { maxLength: notExceedMsg('Dealer Number', 7) }
      );
      const supplierNumberErrors = validation(
        formData.supplierNumber,
        [{ type: ValidationType.maxLength, value: 7 }],
        { maxLength: notExceedMsg('Supplier Number', 7) }
      );

      // Email validations
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const validateEmailList = (emails, totalMaxLen, errorMsg) => {
        if (!emails || !emails.trim()) return [];
        if (emails.length > totalMaxLen) {
          return [errorMsg];
        }
        if (/^,|,,|,$/.test(emails)) {
          return [errorMsg];
        }
        const emailArr = emails.split(',').map(email => email.trim());
        if (emailArr.some(email => !email || !emailPattern.test(email))) {
          return [errorMsg];
        }
        return [];
      };

      const returnEmailsErrors = validateEmailList(
        formData.returnEmails,
        1000,
        'Each email must be a valid email and not exceed 1000 characters.'
      );
      const externalAuctionEmailsErrors = validateEmailList(
        formData.externalAuctionEmails,
        250,
        'Each email must be a valid email and not exceed 250 characters.'
      );
      const additionalEmailsErrors = validateEmailList(
        formData.additionalEmails,
        250,
        'Each email must be a valid email and not exceed 250 characters.'
      );

      const formErrors = {
        locationName: locationNameErrors,
        locationCode: locationCodeErrors,
        externalAuctionCode: externalCodeErrors,
        auctionSystem: auctionSystemErrors,
        address: addressErrors,
        city: cityErrors,
        state: stateErrors,
        zip: zipErrors,
        locationArea: locationAreaErrors,
        saleDays: saleDaysErrors,
        macolaInventoryCode: macolaInventoryCodeErrors,
        returnInstructions: returnInstructionsErrors,
        auctionNotes: auctionNotesErrors,
        titleDays: titleDaysErrors,
        dealerNumber: dealerNumberErrors,
        supplierNumber: supplierNumberErrors,
        returnEmails: returnEmailsErrors,
        externalAuctionEmails: externalAuctionEmailsErrors,
        additionalEmails: additionalEmailsErrors,
      };

      setErrors(formErrors);

      const hasErrors = Object.values(formErrors).some(
        fieldErrors => fieldErrors.length > 0
      );

      if (hasErrors) {
        setLoading(false);
      } else {
        setLoading(true);
        onSubmitHandler({
          ...formData,
          action: locationAction,
        });
      }
    },
    [onSubmitHandler, errors, loading, formData]
  );

  const handleChange = (e, data) => {
    const {
      name, value, checked, type,
    } = data;
    let finalValue;
    if (e instanceof Date || moment.isMoment(e)) {
      finalValue = moment(new Date(e._d)).format('YYYY-MM-DD');
    } else {
      finalValue = type === 'checkbox' ? checked : value;
    }

    if (name === 'saleDays') {
      finalValue = DaysOfWeek.filter(d => finalValue.includes(d));
    }

    setFormData(prev => ({ ...prev, [name]: finalValue }));
    setErrors(prev => ({ ...prev, [name]: [] }));

    // correct auction system option
    if (name === 'locationCode') {
      filterAuctionOptions(finalValue);
    }

    // correct auction system behavior
    if (name === 'auctionSystem') {
      if (!finalValue) {
        setIsRequireExternal(true); // Default required
        setFormData(prev => ({
          ...prev,
          externalAuctionCode: '', // Clear external code
        }));
        setErrors(prev => ({
          ...prev,
          externalAuctionCode: [],
        }));
      } else if (notRequiredExternal.includes(finalValue)) {
        setFormData(prev => ({ ...prev, externalAuctionCode: '' }));
        setIsRequireExternal(false);
        if (errors.externalAuctionCode) {
          setErrors(prev => ({ ...prev, externalAuctionCode: [] }));
        }
      } else {
        setIsRequireExternal(true);
      }
    }
  };

  const onClose = useCallback(() => {
    setFormData(auctionLocationInit);
    setErrors({});
    setLoading(false);
    onCloseError();
    onCloseHandler();
    setIsRequireExternal(true);
  }, [onCloseHandler, onCloseError]);

  const closeError = useCallback(() => {
    onCloseError();
  }, [onCloseError]);

  const modalHeader = useCallback(() => {
    if (locationAction === AuctionLocationAction.Update) {
      return `Edit Auction Location for ${formData.locationName}`;
    }
    return 'New Auction Location';
  }, [locationAction, formData.locationName]);

  return (
    <Modal
      size='large'
      closeIcon
      onClose={onClose}
      open={isModalOpen}
      closeOnEscape
      closeOnRootNodeClick={false}
    >
      <Modal.Description style={{ padding: '20px 20px 0 20px' }}>
        {flashMessage && (
          <Message onDismiss={closeError} content={flashMessage} negative />
        )}
      </Modal.Description>
      <Modal.Header style={{ borderBottom: 'none' }}>
        {modalHeader()}
      </Modal.Header>
      {formData.locationId && (
        <Modal.Description>
          <h4 style={{ paddingLeft: '21px', fontWeight: 'normal' }}>
            Location ID: {formData.locationId}
          </h4>
        </Modal.Description>
      )}
      <Modal.Content>
        <Form>
          <Modal.Description>
            <h4>Auction Details</h4>
            <Form.Group>
              <Form.Field
                width={8}
                required
                error={!isEmpty(errors.locationName)}
              >
                <label htmlFor='locationName'>Name</label>
                <Input
                  id='locationName'
                  name='locationName'
                  autoComplete='off'
                  value={formData.locationName}
                  onChange={handleChange}
                />
                {errors.locationName && (
                  <span className='error-msg'>{errors.locationName}</span>
                )}
              </Form.Field>
              <Form.Field
                width={4}
                required
                error={!isEmpty(errors.locationCode)}
              >
                <label htmlFor='locationCode'>Location / IMS Code</label>
                <Input
                  id='locationCode'
                  className='deactivatable-input'
                  name='locationCode'
                  autoComplete='off'
                  disabled={locationAction === AuctionLocationAction.Update}
                  value={formData.locationCode}
                  onChange={handleChange}
                />
                {errors.locationCode && (
                  <span className='error-msg'>{errors.locationCode}</span>
                )}
              </Form.Field>
              <Form.Field
                width={4}
                required={isRequireExternal}
                error={!isEmpty(errors.externalAuctionCode)}
              >
                <label htmlFor='externalAuctionCode'>External Auction Code</label>
                <Input
                  id='externalAuctionCode'
                  className='deactivatable-input'
                  name='externalAuctionCode'
                  autoComplete='off'
                  disabled={
                    locationAction === AuctionLocationAction.Update
                    || !isRequireExternal
                  }
                  value={formData.externalAuctionCode}
                  onChange={handleChange}
                />
                {errors.externalAuctionCode && (
                  <span className='error-msg'>
                    {errors.externalAuctionCode}
                  </span>
                )}
              </Form.Field>
            </Form.Group>
            <Form.Group>
              <Form.Field
                width={8}
                required
                error={!isEmpty(errors.auctionSystem)}
              >
                <label htmlFor='auctionSystem'>Auction System</label>
                <Select
                  id='auctionSystem'
                  fluid
                  name='auctionSystem'
                  placeholder='Select Product'
                  options={auctionOptions}
                  closeOnBlur
                  selectOnBlur={false}
                  value={formData.auctionSystem}
                  onChange={handleChange}
                />
                {errors.auctionSystem && (
                  <span className='error-msg'>{errors.auctionSystem}</span>
                )}
              </Form.Field>
            </Form.Group>
            <Form.Field>
              <Checkbox
                label='Purchases Enabled'
                name='purchasesEnabled'
                checked={formData.purchasesEnabled}
                onChange={handleChange}
              />
            </Form.Field>
            <Form.Field>
              <Checkbox
                label='Accepts Returns'
                name='acceptsReturn'
                checked={formData.acceptsReturn}
                onChange={handleChange}
              />

              {errors.acceptsReturn && (
                <span className='error-msg'>{errors.acceptsReturn}</span>
              )}
            </Form.Field>
            <Form.Field>
              <Checkbox
                label='Inspections Enabled'
                name='inspectionsEnabled'
                checked={formData.inspectionsEnabled}
                onChange={handleChange}
              />
            </Form.Field>
            <Form.Field>
              <Checkbox
                label='DS Paid PSI Enabled'
                name='dsPaidPsiEnabled'
                checked={formData.dsPaidPsiEnabled}
                onChange={handleChange}
              />
            </Form.Field>
            <Form.Field width={16} error={!isEmpty(errors.saleDays)}>
              <label htmlFor='saleDays'>Sale Days</label>
              <Select
                id='saleDays'
                fluid
                multiple
                search
                selection
                name='saleDays'
                placeholder='Sale Days'
                options={saleDayOption}
                closeOnBlur
                value={formData.saleDays}
                onChange={handleChange}
              />
              {errors.saleDays && (
                <span className='error-msg'>{errors.saleDays}</span>
              )}
            </Form.Field>
            <Form.Group>
              <Form.Field width={4} error={!isEmpty(errors.titleDays)}>
                <label htmlFor='titleDays'>Title Days</label>
                <Input
                  id='titleDays'
                  name='titleDays'
                  className='number-spin-hidden'
                  type='number'
                  autoComplete='off'
                  value={formData.titleDays}
                  onChange={handleChange}
                  onKeyDown={e => filterNonDigitInput(e)}
                />
                {errors.titleDays && (
                  <span className='error-msg'>{errors.titleDays}</span>
                )}
              </Form.Field>
              <Form.Field width={4}>
                <label htmlFor='launchDate'>Launch Date</label>
                <DatePicker
                  id='launchDate'
                  style={{ padding: '0', width: '100%' }}
                  selected={
                    formData.launchDate ? utcMoment(formData.launchDate) : null
                  }
                  onChange={date => handleChange(date, { name: 'launchDate' })}
                  dateFormat='MM/DD/YYYY'
                  name='launchDate'
                  autoComplete='offline'
                />
              </Form.Field>
            </Form.Group>
            <Form.Group>
              <Form.Field width={8} required error={!isEmpty(errors.address)}>
                <label htmlFor='address'>Address</label>
                <Input
                  id='address'
                  name='address'
                  autoComplete='off'
                  value={formData.address}
                  onChange={handleChange}
                />
                {errors.address && (
                  <span className='error-msg'>{errors.address}</span>
                )}
              </Form.Field>
              <Form.Field width={8} required error={!isEmpty(errors.city)}>
                <label htmlFor='city'>City</label>
                <Input
                  id='city'
                  name='city'
                  autoComplete='off'
                  value={formData.city}
                  onChange={handleChange}
                />
                {errors.city && (
                  <span className='error-msg'>{errors.city}</span>
                )}
              </Form.Field>
            </Form.Group>

            <Form.Group widths={3}>
              <Form.Field required error={!isEmpty(errors.state)}>
                <label htmlFor='state'>State</label>
                <Select
                  id='state'
                  search
                  name='state'
                  placeholder='Select State'
                  options={LocationState}
                  closeOnBlur
                  value={formData.state}
                  onChange={handleChange}
                />
                {errors.state && (
                  <span className='error-msg'>{errors.state}</span>
                )}
              </Form.Field>
              <Form.Field required error={!isEmpty(errors.zip)}>
                <label htmlFor='zip'>Zip</label>
                <Input
                  id='zip'
                  name='zip'
                  className='number-spin-hidden'
                  type='number'
                  autoComplete='off'
                  value={formData.zip}
                  onChange={handleChange}
                  onKeyDown={e => filterNonDigitInput(e)}
                />
                {errors.zip && <span className='error-msg'>{errors.zip}</span>}
              </Form.Field>
              <Form.Field error={!isEmpty(errors.locationArea)}>
                <label htmlFor='locationArea'>Location Area</label>
                <Input
                  id='locationArea'
                  name='locationArea'
                  autoComplete='off'
                  value={formData.locationArea}
                  onChange={handleChange}
                />
                {errors.locationArea && (
                  <span className='error-msg'>{errors.locationArea}</span>
                )}
              </Form.Field>
            </Form.Group>
          </Modal.Description>
          <Modal.Description>
            <h4 style={{ paddingTop: '14px', borderTop: '1px solid #b3b3b3' }}>
              Disbursement / Billing Details
            </h4>
            <Form.Group widths={3}>
              <Form.Field error={!isEmpty(errors.dealerNumber)}>
                <label htmlFor='dealerNumber'>Dealer Number</label>
                <Input
                  id='dealerNumber'
                  name='dealerNumber'
                  className='number-spin-hidden'
                  type='number'
                  autoComplete='off'
                  value={formData.dealerNumber}
                  onChange={handleChange}
                  onKeyDown={e => filterNonDigitInput(e)}
                />
                {errors.dealerNumber && (
                  <span className='error-msg'>{errors.dealerNumber}</span>
                )}
              </Form.Field>
              <Form.Field error={!isEmpty(errors.supplierNumber)}>
                <label htmlFor='supplierNumber'>Supplier Number</label>
                <Input
                  id='supplierNumber'
                  name='supplierNumber'
                  className='number-spin-hidden'
                  type='number'
                  autoComplete='off'
                  value={formData.supplierNumber}
                  onChange={handleChange}
                  onKeyDown={e => filterNonDigitInput(e)}
                />
                {errors.supplierNumber && (
                  <span className='error-msg'>{errors.supplierNumber}</span>
                )}
              </Form.Field>
              <Form.Field error={!isEmpty(errors.macolaInventoryCode)}>
                <label htmlFor='macolaInventoryCode'>Macola Inventory Code</label>
                <Input
                  id='macolaInventoryCode'
                  name='macolaInventoryCode'
                  autoComplete='off'
                  value={formData.macolaInventoryCode}
                  onChange={handleChange}
                />
                {errors.macolaInventoryCode && (
                  <span className='error-msg'>
                    {errors.macolaInventoryCode}
                  </span>
                )}
              </Form.Field>
            </Form.Group>
          </Modal.Description>
          <Modal.Description>
            <h4 style={{ paddingTop: '14px', borderTop: '1px solid #b3b3b3' }}>
              Email Settings
            </h4>
            <Form.Group widths={3}>
              <Form.Field error={!isEmpty(errors.returnEmails)}>
                <label htmlFor='returnEmails'>Return Emails</label>
                <Input
                  id='returnEmails'
                  name='returnEmails'
                  autoComplete='off'
                  value={formData.returnEmails}
                  onChange={handleChange}
                />
                {errors.returnEmails && (
                  <span className='error-msg'>{errors.returnEmails}</span>
                )}
              </Form.Field>
              <Form.Field error={!isEmpty(errors.externalAuctionEmails)}>
                <label htmlFor='externalAuctionEmails'>External Auctions Email</label>
                <Input
                  id='externalAuctionEmails'
                  name='externalAuctionEmails'
                  autoComplete='off'
                  value={formData.externalAuctionEmails}
                  onChange={handleChange}
                />
                {errors.externalAuctionEmails && (
                  <span className='error-msg'>{errors.externalAuctionEmails}</span>
                )}
              </Form.Field>
              <Form.Field error={!isEmpty(errors.additionalEmails)}>
                <label htmlFor='additionalEmails'>Additional Email</label>
                <Input
                  id='additionalEmails'
                  name='additionalEmails'
                  autoComplete='off'
                  value={formData.additionalEmails}
                  onChange={handleChange}
                />
                {errors.additionalEmails && (
                  <span className='error-msg'>
                    {errors.additionalEmails}
                  </span>
                )}
              </Form.Field>
            </Form.Group>
          </Modal.Description>
          <Modal.Description>
            <h4 style={{ paddingTop: '14px', borderTop: '1px solid #b3b3b3' }}>
              Additional Information
            </h4>
            <Form.Group>
              <Form.Field width={8} error={!isEmpty(errors.returnInstructions)}>
                <label htmlFor='returnInstructions'>Return Instructions</label>
                <TextArea
                  id='returnInstructions'
                  name='returnInstructions'
                  style={{ resize: 'none' }}
                  value={formData.returnInstructions}
                  onChange={handleChange}
                />
                {errors.returnInstructions && (
                  <span className='error-msg'>{errors.returnInstructions}</span>
                )}
              </Form.Field>
              <Form.Field width={8} error={!isEmpty(errors.auctionNotes)}>
                <label htmlFor='auctionNotes'>Auction Notes</label>
                <TextArea
                  id='auctionNotes'
                  name='auctionNotes'
                  style={{ resize: 'none' }}
                  value={formData.auctionNotes}
                  onChange={handleChange}
                />
                {errors.auctionNotes && (
                  <span className='error-msg'>{errors.auctionNotes}</span>
                )}
              </Form.Field>
            </Form.Group>
          </Modal.Description>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color='green' loading={loading} onClick={submitForm}>
          Submit
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

AuctionLocationForm.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  locationAction: PropTypes.string,
  submitError: PropTypes.string,
  selectedLocation: PropTypes.shape({
    locationId: PropTypes.string,
    locationName: PropTypes.string,
    locationCode: PropTypes.string,
    externalAuctionCode: PropTypes.string,
    auctionSystem: PropTypes.string,
    purchasesEnabled: PropTypes.bool,
    acceptsReturn: PropTypes.bool,
    inspectionsEnabled: PropTypes.bool,
    dsPaidPsiEnabled: PropTypes.bool,
    saleDays: PropTypes.arrayOf(PropTypes.string),
    titleDays: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    launchDate: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    locationArea: PropTypes.string,
    dealerNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    supplierNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    macolaInventoryCode: PropTypes.string,
    returnInstructions: PropTypes.string,
    auctionNotes: PropTypes.string,
    returnEmails: PropTypes.string,
    externalAuctionEmails: PropTypes.string,
    additionalEmails: PropTypes.string,
  }),
  onSubmitHandler: PropTypes.func.isRequired,
  onCloseHandler: PropTypes.func.isRequired,
  onCloseError: PropTypes.func.isRequired,
};

export default AuctionLocationForm;
